import React, { useCallback } from 'react'
import styled, { useTheme } from 'styled-components'
import { BodyFlow, TertiaryButton, useModal } from '@monthio/react-components'
import { useTranslation } from 'react-i18next'
import { useMediaQuery } from '@material-ui/core'
import { Asset, overarchingTypeOf } from '../AssetOverview.types'
import { Column } from '#modules/DebtOverview/Components/column'

const ModalContainer = styled.div`
	padding: 8px 24px 24px 24px;
`

const DeleteAssetModal: React.FC<{
	close: (willDelete: boolean) => void
	subtitle: string
}> = ({ close, subtitle }) => {
	const { t } = useTranslation()
	return (
		<ModalContainer>
			<BodyFlow>{subtitle}</BodyFlow>
			<Column>
				<TertiaryButton onClick={() => close(true)}>
					{t('btn-yes')}
				</TertiaryButton>
				<TertiaryButton onClick={() => close(false)}>
					{t('btn-cancel')}
				</TertiaryButton>
			</Column>
		</ModalContainer>
	)
}

export const useDeleteAssetModal = ({
	onDelete,
	asset,
}: {
	onDelete: (asset: Asset) => void
	asset: Asset
}): { openDeleteModal: () => Promise<void> } => {
	const { t } = useTranslation()

	const { title, subtitle } = {
		realEstate: {
			title: t('assetOverview-deleteModal-realEstate-title'),
			subtitle: t('assetOverview-deleteModal-realEstate-sub-title'),
		},
		vehicle: {
			title: t('assetOverview-deleteModal-vehicle-title'),
			subtitle: t('assetOverview-deleteModal-vehicle-sub-title'),
		},
	}[overarchingTypeOf(asset.assetType)]

	const { openModal } = useModal()
	const theme = useTheme()
	const isSmallScreen = useMediaQuery(theme.breakpoints.down('xs'))
	const openDeleteModal = useCallback(async () => {
		const willDelete = await openModal({
			bodyRender: function RenderDeleteModal({ close }) {
				return <DeleteAssetModal close={close} subtitle={subtitle} />
			},
			fullScreen: isSmallScreen,
			title,
		})
		willDelete && onDelete(asset)
	}, [asset, isSmallScreen, onDelete, openModal, subtitle, title])
	return { openDeleteModal }
}
